import { FoodCategory } from "@corpuscomplete/core/nutrition/food-category";
import { queries } from "@corpuscomplete/core/zero";
import { useQuery } from "@corpuscomplete/zero-data-transformer/react";
import { useMemo } from "react";

export namespace FoodCategoryUI {
  export type SearchOptions = {
    limit?: number;
    excludeIds?: readonly string[];
  };

  const DEFAULT_SEARCH_LIMIT = 20;

  function normalizeSearchText(text: string) {
    return text
      .trim()
      .toLowerCase()
      .replace(/[_\s-]+/g, " ");
  }

  function searchRank(category: FoodCategory.Info, normalizedText: string) {
    const normalizedName = normalizeSearchText(category.name);
    const normalizedKey = normalizeSearchText(category.key);

    if (normalizedName === normalizedText || normalizedKey === normalizedText) {
      return 0;
    }
    if (normalizedName.startsWith(normalizedText) || normalizedKey.startsWith(normalizedText)) {
      return 1;
    }
    if (normalizedName.includes(normalizedText) || normalizedKey.includes(normalizedText)) {
      return 2;
    }
    return null;
  }

  export function useByIds(categoryIds: readonly string[]): FoodCategory.Info[] {
    const [categories] = useQuery(queries.foodCategory.all());

    return useMemo(() => {
      const categoriesById = new Map(categories.map((category) => [category.id, category]));
      return categoryIds.flatMap((id) => {
        const category = categoriesById.get(id);
        return category ? [category] : [];
      });
    }, [categories, categoryIds]);
  }

  export function useByKeys(categoryKeys: readonly string[]): FoodCategory.Info[] {
    const [categories] = useQuery(queries.foodCategory.all());

    return useMemo(() => {
      const categoriesByKey = new Map(categories.map((category) => [category.key, category]));
      return categoryKeys.flatMap((key) => {
        const category = categoriesByKey.get(key);
        return category ? [category] : [];
      });
    }, [categories, categoryKeys]);
  }

  export function useSearch(text: string, options?: SearchOptions): FoodCategory.Info[] {
    const [categories] = useQuery(queries.foodCategory.all());
    const excludeIds = options?.excludeIds;
    const limit = options?.limit ?? DEFAULT_SEARCH_LIMIT;

    return useMemo(() => {
      const normalizedText = normalizeSearchText(text);
      if (!normalizedText) {
        return [];
      }

      const excluded = new Set(excludeIds ?? []);
      return categories
        .flatMap((category) => {
          if (excluded.has(category.id)) {
            return [];
          }

          const rank = searchRank(category, normalizedText);
          return rank === null ? [] : [{ category, rank }];
        })
        .sort(
          (left, right) =>
            left.rank - right.rank || left.category.name.localeCompare(right.category.name),
        )
        .slice(0, limit)
        .map(({ category }) => category);
    }, [categories, excludeIds, limit, text]);
  }
}
